import type {
  IAssetTemplateModalDataResult,
  IAssetTemplateModalAssignedResult,
  IAssetTemplateListingResult,
} from '../hooks';
import type { IBaseCampaign } from '~/modules/campaign/types';
import type { IQueryThemePages } from '~/modules/apps';
import type { ReactNode } from 'react';

import {
  useInitializeAssetTemplateModalData,
  useInitializeAssetTemplateListing,
  useInitializeAssetTemplateModalAssigned,
} from '../hooks';
import { createContext, useContext, useMemo } from 'react';
import { useQueryThemePages } from '~/modules/apps';

export interface IAssetTemplateContext extends IAssetTemplateListingResult {
  campaigns: IBaseCampaign[];
  themePages: IQueryThemePages['themePages'];
  modalAssetTemplate: IAssetTemplateModalDataResult;
  modalAssetTemplateAssigned: IAssetTemplateModalAssignedResult;
}
export interface IAssetTemplateProvider {
  children: ReactNode;
  campaigns: IBaseCampaign[];
}

export const AssetTemplateContext = createContext<IAssetTemplateContext | undefined>(undefined);

export const AssetTemplateProvider: React.FC<IAssetTemplateProvider> = ({
  children,
  campaigns,
}) => {
  const assetTemplateListing = useInitializeAssetTemplateListing();
  const assetTemplateModalData = useInitializeAssetTemplateModalData();
  const assetTemplateModalAssigned = useInitializeAssetTemplateModalAssigned();

  const { themePages } = useQueryThemePages({
    campaigns,
  });

  const contextValue = useMemo(
    () => ({
      campaigns,
      themePages,
      modalAssetTemplate: assetTemplateModalData,
      modalAssetTemplateAssigned: assetTemplateModalAssigned,
      ...assetTemplateListing,
    }),
    [
      assetTemplateModalData,
      assetTemplateListing,
      assetTemplateModalAssigned,
      themePages,
      campaigns,
    ],
  );

  return (
    <AssetTemplateContext.Provider value={contextValue}>{children}</AssetTemplateContext.Provider>
  );
};

export const useAssetTemplateContext = () => {
  const context = useContext(AssetTemplateContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context;
};
